import classNames from 'classnames'
import { get } from 'lodash'
import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react'
import { FormProps, TouchedFields, useFormContext, ValidatedForm } from 'remix-validated-form'
import { twMerge } from 'tailwind-merge'
import Button from '../Button'
import FormHiddenInput from './FormHiddenInput'
import { trace } from 'lib/logging'

const NOOP = () => {}
const GO_BACK = () => history.back()

type SmartFormProps<
  DataType extends {
    [fieldName: string]: any
  },
> = {
  id?: string
  className?: string
  encType?: string
  method?: string
  showDelete?: boolean
  hideButtons?: boolean
  replace?: boolean
  shouldPreventDefault?: boolean
  hiddenInputs?: { name: string; value?: string | string[] }[]
  preventSubmit?: boolean
  addDefaultClass?: boolean
  formClassNames?: string
  onTouchedFields?: (touched: boolean, touchedFields?: TouchedFields) => void
  onChange?: (data?: FormEvent<HTMLFormElement>) => void
  onCancel?: () => void
  onDelete?: () => void
  onPreventDefault?: (args?: any) => void
  onSubmit?: (data?: DataType) => void
  onValidationError?: (errors: Record<string, string>) => void
  formRef?: React.RefObject<HTMLFormElement | null>
} & Omit<FormProps<DataType, any>, 'formRef'>

export enum SubAction {
  CREATE = `create`,
  EDIT = `edit`,
  DELETE = `delete`,
  ARCHIVE = `archive`,
}
/**
 * Inner component that lives inside ValidatedForm's FormContext.Provider.
 * It reactively observes touchedFields via Context (not formRegistry),
 * then notifies SmartForm (the parent) of dirty state changes.
 */
function DirtyFieldsObserver({
  defaultValues,
  onFieldsChange,
}: {
  defaultValues?: Record<string, unknown>
  onFieldsChange?: (touched: boolean, touchedFields?: TouchedFields) => void
}): ReactElement | null {
  // Called without formId so it reads from React Context (reactive)
  const formContext = useFormContext()

  useEffect(() => {
    if (!onFieldsChange) return
    let fieldsChanged = Object.entries(formContext.touchedFields).filter(([k, v]) => {
      if (!v) return false
      let defaultValue = get(defaultValues, k) ?? ``

      // For object-typed fields (e.g. leadOwner), string coercion produces "[object Object]"
      // which makes FormData comparison useless. The field components (TypedAsyncComboV2)
      // already use isEqual to correctly set touchedFields, so trust the boolean directly.
      if (typeof defaultValue === 'object' && defaultValue !== null) {
        return true
      }

      let formValues = formContext.getValues()
      return (
        (formValues.has(k) && `${defaultValue}` !== formValues.get(k)) ||
        (formValues.has(`${k}[value]`) && `${defaultValue}` !== formValues.get(`${k}[value]`)) ||
        !formValues.has(k)
      )
    })
    onFieldsChange(fieldsChanged.length > 0, formContext.touchedFields)
  }, [formContext.touchedFields])

  return null
}

/**
 * Main form component to be used which wraps remix-validated-form's ValidatedForm component.
 * @summary Use this to create a form in a page, passing as children only wrapped inputs such as FormInput, FormSelect, etc.
 * @param {SmartFormProps<DataType>} SmartFormProps - Pass in both props to be passed down to ValidatedForm and props for SmartForm such as children, hiddenInputs, etc.
 * @return {ReturnValueDataTypeHere} Gives you a ValidatedForm with hidden inputs, default form buttons and lets you insert whatever inputs you need.
 */

export function SmartForm<
  DataType extends {
    [fieldName: string]: any
  },
>({
  id = `smartForm`,
  method = `post`,
  className = ``,
  encType = `application/x-www-form-urlencoded`,
  replace = true,
  showDelete = false,
  hideButtons = false,
  shouldPreventDefault = false,
  validator,
  defaultValues,
  hiddenInputs,
  children,
  subaction,
  preventSubmit = false,
  formClassNames,
  addDefaultClass = false,
  onCancel = GO_BACK,
  onTouchedFields: onFieldsChange,
  onChange,
  onDelete = NOOP,
  onPreventDefault = NOOP,
  onSubmit,
  onValidationError,
  action,
  formRef,
}: SmartFormProps<DataType>) {
  const formContext = useFormContext(id)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const submittingRef = useRef(false)
  const [preventDefaultData, setPreventDefaultData] = useState<any>(undefined)
  const internalFormRef = useRef<HTMLFormElement>(null)
  const effectiveFormRef = formRef ?? internalFormRef
  const getHiddenInputErrors = () => {
    return Object.entries(formContext.fieldErrors).filter(
      ([name, error]) => error && hiddenInputs?.find((hidden) => hidden.name === name),
    )
  }

  const handleSubmit = (data: DataType, event: React.FormEvent<HTMLFormElement>) => {
    trace(`form`, `handleSubmit`, id, data, preventSubmit)
    if (submittingRef.current) return
    const form = effectiveFormRef.current
    if (preventSubmit) {
      submittingRef.current = true
      setIsSubmitting(true)
      event.preventDefault()
      event.stopPropagation()
    }
    if (shouldPreventDefault && action) {
      event.preventDefault()
      event.stopPropagation()
      if (!form) return
      submittingRef.current = true
      setIsSubmitting(true)
      const formData = new FormData(form)
      window
        .fetch(action, {
          method: method || `post`,
          credentials: `include`,
          body: formData,
        })
        .then((r) => r.json())
        .then((d) => {
          setPreventDefaultData(d)
        })
        .catch(() => {})
        .finally(() => {
          submittingRef.current = false
          setIsSubmitting(false)
        })
    }
    const result = onSubmit?.(data)
    if (preventSubmit && !(shouldPreventDefault && action)) {
      Promise.resolve(result).finally(() => {
        submittingRef.current = false
        setIsSubmitting(false)
      })
    }
  }

  useEffect(() => {
    if (shouldPreventDefault && preventDefaultData) {
      onPreventDefault?.(preventDefaultData)
    }
  }, [preventDefaultData])

  useEffect(() => {
    if (Object.entries(formContext.fieldErrors).length) {
      // eslint-disable-next-line no-console
      console.error(`Form errors`, id, formContext.fieldErrors)
      onValidationError?.(formContext.fieldErrors)
    }
  }, [formContext.fieldErrors])

  return (
    <>
      <ValidatedForm
        encType={encType}
        replace={replace}
        validator={validator}
        defaultValues={defaultValues}
        subaction={subaction}
        method={method}
        action={action}
        id={id}
        autoComplete="off"
        fetcher={undefined}
        onSubmit={handleSubmit}
        onChange={(val) => onChange?.(val)}
        className={classNames(formClassNames, { 'flex grow overflow-hidden': addDefaultClass })}
        formRef={effectiveFormRef}
      >
        <DirtyFieldsObserver defaultValues={defaultValues} onFieldsChange={onFieldsChange} />
        {/* Mobile: flex column so every child stacks full-width (grid-column spans
            are ignored under display:flex). At >=sm revert to the 6-col grid, so
            col-span-* fields lay out exactly as before on desktop. Base grid-cols-1
            alone doesn't stack: col-span-6 children spawn implicit tracks (12 cols)
            and pack two-per-row. */}
        <div className={twMerge(`mt-6 flex max-w-3xl flex-col gap-x-4 gap-y-6 sm:grid sm:grid-cols-6`, className)}>
          {hiddenInputs &&
            hiddenInputs?.map((hiddenInput, i) => (
              <FormHiddenInput key={i} name={hiddenInput.name} value={hiddenInput.value} />
            ))}
          {children}
          {getHiddenInputErrors().map(([k, error], i) => (
            <p key={`${k}-${i}`} className="col-span-6 mt-4 text-red-500">
              {error}
            </p>
          ))}
          {!hideButtons && (
            <div data-sticky-footer className="sticky bottom-4 z-10 col-span-6 my-4 bg-surface-raised">
              <div className="flex justify-end gap-4">
                <Button label="Cancel" mode="white" size="md" onClick={onCancel} />
                {showDelete && <Button label="Delete" size="md" mode="secondary" onClick={() => onDelete()} />}

                <Button type="submit" label="Save" mode="primary" size="md" loading={isSubmitting} />
              </div>
            </div>
          )}
        </div>
      </ValidatedForm>
    </>
  )
}
